import React from 'react';
import { Link } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { useTheme } from '../context/ThemeContext';
import { 
  FaArrowLeft as FaArrowLeftBase, 
  FaPython as FaPythonBase, 
  FaReact as FaReactBase, 
  FaDatabase as FaDatabaseBase 
} from 'react-icons/fa';

// Fix for TS2786
const FaArrowLeft = FaArrowLeftBase as React.ElementType;
const FaPython = FaPythonBase as React.ElementType;
const FaReact = FaReactBase as React.ElementType;
const FaDatabase = FaDatabaseBase as React.ElementType;

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, Filler);

const SalesDashboard: React.FC = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const lineData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [
      {
        label: 'Revenue 2024',
        data: [30000, 45000, 42000, 50000, 75000, 82000],
        borderColor: '#8b5cf6',
        backgroundColor: 'rgba(139, 92, 246, 0.1)',
        fill: true,
        tension: 0.4,
      },
      {
        label: 'Projected',
        data: [null, null, null, null, null, 82000, 95000, 110000],
        borderColor: '#9ca3af',
        borderDash: [5, 5],
        tension: 0.4,
      }
    ],
  };

  return (
    <div className="min-h-screen bg-secondary pt-20 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/portfolio" className="inline-flex items-center text-secondary hover:text-accent mb-8 transition-colors">
          <FaArrowLeft className="mr-2" /> Back to Portfolio
        </Link>

        <div className="mb-12">
          <h1 className="text-4xl font-bold text-primary mb-4">E-Commerce Intelligence Platform</h1>
          <p className="text-xl text-secondary max-w-3xl">
            A comprehensive analytics suite combining historical sales data with predictive modeling to forecast inventory needs.
          </p>
        </div>

        {/* Dashboard UI */}
        <div className="bg-primary rounded-xl shadow-lg border border-primary p-6 mb-12">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-xl font-bold text-primary">Executive Overview</h2>
            <div className="flex gap-2">
              <button className="px-3 py-1 text-sm bg-accent text-white rounded-md">Monthly</button>
              <button className="px-3 py-1 text-sm bg-secondary text-secondary rounded-md border border-primary">Quarterly</button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="p-6 rounded-xl bg-gradient-to-br from-purple-600 to-indigo-700 text-white shadow-lg">
              <p className="text-purple-100 text-sm mb-1">Total Revenue</p>
              <h3 className="text-3xl font-bold">$324,000</h3>
              <p className="text-xs mt-2 text-purple-200">↑ 12% vs last month</p>
            </div>
            <div className="p-6 rounded-xl bg-secondary border border-primary">
              <p className="text-secondary text-sm mb-1">Conversion Rate</p>
              <h3 className="text-3xl font-bold text-primary">3.2%</h3>
              <p className="text-xs mt-2 text-green-500">↑ 0.4% improvement</p>
            </div>
            <div className="p-6 rounded-xl bg-secondary border border-primary">
              <p className="text-secondary text-sm mb-1">Avg Order Value</p>
              <h3 className="text-3xl font-bold text-primary">$145</h3>
              <p className="text-xs mt-2 text-red-500">↓ 2% vs last month</p>
            </div>
          </div>

          <div className="h-80 w-full">
            <Line 
              data={lineData} 
              options={{ 
                responsive: true, 
                maintainAspectRatio: false,
                plugins: { legend: { position: 'top' } },
                scales: {
                  y: { grid: { color: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' } },
                  x: { grid: { display: false } }
                }
              }} 
            />
          </div>
        </div>

        {/* Technical Deep Dive */}
        <div className="grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2">
            <h3 className="text-2xl font-bold text-primary mb-4">Technical Implementation</h3>
            <p className="text-secondary mb-4">
              The core of this application is a Python-based ETL pipeline that ingests raw transaction logs from SQL databases. 
              Data is cleaned using Pandas and served via a REST API.
            </p>
            <p className="text-secondary">
              For the frontend, I implemented React Context for state management to handle complex filtering logic without prop drilling. 
              The forecasting module uses a simple linear regression model (visualized by the dotted line) to help stakeholders plan inventory.
            </p>
          </div>
          <div className="bg-primary p-6 rounded-xl border border-primary h-fit">
            <h4 className="font-bold text-primary mb-4">Stack</h4>
            <ul className="space-y-2 text-secondary text-sm">
              <li className="flex items-center"><FaReact className="mr-2 text-blue-400"/> React (Frontend)</li>
              <li className="flex items-center"><FaPython className="mr-2 text-yellow-400"/> Python / Pandas (Data)</li>
              <li className="flex items-center"><FaDatabase className="mr-2 text-green-400"/> PostgreSQL (Storage)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesDashboard;